import * as d3 from "d3";
import {interpolateRgb} from "d3";
import type {PaletteOptions, Theme} from "@mui/material";
import {darken} from "@mui/material";

declare module '@mui/material/styles' {
    interface Color {
        150?: string;
        1000?: string;
    }
    interface Palette {
        mithraBlue: {
            100: string;
            200: string;
            300: string;
            500: string;
            700: string;
            800: string;
            900: string;
        };
        black: {
            400: string;
            500: string;
            600: string;
            700: string;
            800: string;
            900: string;
        };
        white: {
            700: string;
            800: string;
            900: string;
        };
    }
    interface PaletteOptions {
        mithraBlue?: Partial<Palette['mithraBlue']>;
        black?: Partial<Palette['black']>;
        white?: Partial<Palette['white']>;
    }
}

// https://material.io/design/color/the-color-system.html
// https://material.io/resources/color/
// https://color.adobe.com/

export const COLOR_MITHRA_DARK_BLUE = '#001A40';
export const COLOR_MITHRA_LIGHT_BLUE = '#478FFA';
export const COLOR_UNCATEGORIZED = '#de4b1e';

export const COLORS = {
    primary: {
        100: 'rgba(64, 136, 248, 0.10)',
        300:'rgba(150, 191, 255, 0.30)',
        400: '#D4DFFD',
        500: '#4088F8',
        700: '#1565C0',
        800: '#082B5B',
        900: '#061D3C',
        main: '#082B5B',
        dark: '#061D3C'
    },
    secondary: {
        100: '#F3CAFA',
        300: 'rgba(211, 61, 236, 0.30)',
        500: '#9C27B0',
        700:'#7B1FA2',
        900: '#55196F',
        main: '#9C27B0',
        dark: '#7B1FA2',
    },
    text: {
        primary: '#1A1B20',
        secondary: 'rgba(25,48,79,0.54)',
    },
    divider: '#C4C6D0',
    success: {
        100: 'rgba(152, 226, 156, 0.30)',
        300: '#C0D8C1',
        500: '#2E7D32',
        700:'#076600',
        900: '#1E4620',
        main: '#2E7D32',
        dark: '#076600',
    },
    warning: {
        100: 'rgba(255, 176, 110, 0.30)',
        300: '#FAD2B2',
        500: '#EF6C00',
        700:'#CC4800',
        900: '#663C00',
        main: '#EF6C00',
        dark: '#CC4800',
    },
    error: {
        100: 'rgba(255, 134, 134, 0.30)',
        300: '#F1C0C0',
        500: '#BA1A1A',
        700:'#93000A',
        900: '#600004',
        main: '#BA1A1A',
        dark: '#93000A',
    },
    info: {
        main: '#4088F8',
        dark: '#1565C0'
    },
    grey: {
        50: '#F3F3FA',
        100: '#E2E2E9',
        150: '#EDEDF4',
        200: '#C7C6CA',
        300: '#C4C6D0',
        400: '#ABABAF',
        500: '#919094',
        600: '#77777A',
        700: '#5E5E62',
        800: '#525256',
        900: '#46464A',
        1000: '#3B3B3F',
    },
    mithraBlue: {
        100: 'rgba(64, 136, 248, 0.10)',
        200:'rgba(150, 191, 255, 0.30)',
        300: '#D4DFFD',
        500: '#4088F8',
        700: '#1565C0',
        800: '#082B5B',
        900: '#061D3C'
    },
    black: {
        400: '#44474E',
        500: '#2F3033',
        600:'#252629',
        700:'#1B1B1F',
        800:'#101114',
        900: '#000'
    },
    white: {
        700:'#FAF9FD',
        800:'#FEFBFF',
        900: '#FFF'
    }
} satisfies PaletteOptions;

// https://github.com/d3/d3-scale-chromatic/blob/master/README.md
const categoryColors = [COLORS.primary.main, COLOR_MITHRA_LIGHT_BLUE]
export const SUPPLIER_CUMULATIVE_COLOR = darken(categoryColors[0], 0.5);

export const categoryColorGradient = interpolateRgb.gamma(2.2)(categoryColors[0], categoryColors[1]);
export const primaryColorGradient = categoryColorGradient;

const colorSteps = 15;
export const vizColor = d3.scaleOrdinal([...Array(colorSteps)].map((_, i) =>
    // primaryColorGradient(i / (colorSteps - 1) * 0.3)
    primaryColorGradient(i / (colorSteps - 1))
));

export function getColorByCat(category: string | null): string {
    if (category === '' || category === null) {
        return COLOR_UNCATEGORIZED;
    } else {
        return vizColor(category);
    }
}

export function getColorMonotone(theme: Theme | any, category: string | null): string {
    if (category === '' || category === null) {
        return COLOR_UNCATEGORIZED;
    }
    if (!theme.palette?.primary?.main) {
        console.warn('Migration breaking change from MUI v4')
        return COLORS.primary.main;
    }
    return theme.palette.primary.main;
}
